import { AppModuleDef, DiagnosisOption, NewPatientFormState, PatientProfile, ProcedureSessionRow } from "./types";

// ==========================================
// Application Module Routing Configuration
// ==========================================

/** * Centralized configuration for main application modules. 
 * Use this array to map sidebar links, dashboard grids, and router definitions.
 */
export const CAREFLOW_MODULES: AppModuleDef[] = [
    { id: 'reception', label: 'Reception Desk', icon: '🏥', path: '/reception', color: '#f43f5e', description: 'Patient intake & scheduling' },
    { id: 'doctor', label: 'Doctor Hub', icon: '👨⚕️', path: '/doctor', color: '#3b82f6', description: 'Clinical workspace & charting' },
    { id: 'clinical', label: 'Clinical Setup', icon: '🔬', path: '/treatments', color: '#10b981', description: 'Manage treatments & catalogs' },
    { id: 'pharmacy', label: 'Dispensary', icon: '💊', path: '/pharmacy', color: '#f59e0b', description: 'Inventory & drug dispensing' },
    { id: 'manager', label: 'Analytics & Finance', icon: '📈', path: '/manager', color: '#8b5cf6', description: 'Clinic performance & ledgers' },
    { id: 'followup', label: 'Treatment Followup', icon: '📞', path: '/followups', color: '#ec4899', description: 'Patient followups & recalls' },
];

// ==========================================
// Formatting & String Utilities
// ==========================================

export const blankPatientForm: NewPatientFormState = {
    mobile: "",
    name: "",
    age: "",
    gender: "",
    area: "",
};

export const normalize = (value: string): string =>
    value.trim().toLowerCase().replace(/\s+/g, " ");

export const statusToken = (status?: string): string =>
    String(status || "").trim().toLowerCase().replace(/\s+/g, "_");

export const isCompletedStatus = (status?: string): boolean => {
    const token = statusToken(status);
    return token === "completed" || token === "done";
};

export const parseToothList = (raw: string): string[] =>
    raw
        .split(/[\s,]+/)
        .map((v) => v.trim())
        .filter(Boolean)
        .map((v) => v.toUpperCase());

export const diagnosisLabel = (item: DiagnosisOption): string =>
    String(item.description || item.diagnosis || "").trim();

/** Safely formats a date string into a readable format */
export const formatWhen = (raw?: string): string => {
    if (!raw) return "N/A";
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return "Invalid Date";
    return d.toLocaleString([], {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
    });
};

/** Standardizes INR currency formatting across the app */
export const formatCurrency = (amount: number | string | null | undefined): string => {
    const parsed = Number(amount);
    if (Number.isNaN(parsed)) return "₹0.00";
    return `₹${parsed.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/** Extracts a 1-2 character initial for user avatars */
export const getInitials = (name?: string): string => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.slice(0, 2).toUpperCase();
};

export function statusClass(s?: string): string {
  const v = (s || "").toLowerCase();
  if (v.includes("progress") || v.includes("chair")) return "active";
  if (v.includes("wait") || v.includes("checked")) return "waiting";
  if (v.includes("complete") || v.includes("lock") || v.includes("done")) return "done";
  return "";
}

export function toIsoDate(d = new Date()): string {
  const x = new Date(d);
  x.setMinutes(x.getMinutes() - x.getTimezoneOffset());
  return x.toISOString().slice(0, 10);
}

// ==========================================
// Data Mapping & Merging Utilities
// ==========================================

export function mapDiagnosisRows(input: unknown): DiagnosisOption[] {
    if (!Array.isArray(input)) return [];
    return input.map((row) => row as DiagnosisOption);
}

export function mapSessions(input: unknown): ProcedureSessionRow[] {
    if (!Array.isArray(input)) return [];
    return (input as ProcedureSessionRow[]).slice().sort((a, b) => {
        const aTime = Date.parse(String(a.created_at || "")) || 0;
        const bTime = Date.parse(String(b.created_at || "")) || 0;
        if (aTime !== bTime) return bTime - aTime;
        return Number(b.id || 0) - Number(a.id || 0);
    });
}

export function mergeDiagnosisOptions(...groups: DiagnosisOption[][]): DiagnosisOption[] {
    const seen = new Set<string>();
    const out: DiagnosisOption[] = [];
    for (const group of groups) {
        for (const row of group) {
            const label = diagnosisLabel(row);
            if (!label) continue;
            const key = normalize(label);
            if (seen.has(key)) continue;
            seen.add(key);
            out.push(row);
        }
    }
    return out;
}

export function toPatientProfile(raw: unknown, fallback: NewPatientFormState): PatientProfile {
    const value = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
    const patientId = String(value.patient_id || fallback.mobile);
    return {
        patient_id: patientId,
        name: String(value.name || fallback.name),
        mobile: String(value.mobile || value.phone || fallback.mobile),
        age: value.age != null ? Number(value.age) : undefined,
        gender: value.gender ? String(value.gender) : fallback.gender,
        area: value.area ? String(value.area) : fallback.area,
        phone: value.phone ? String(value.phone) : fallback.mobile,
    };
}
